import { bookingPriceAud } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { revenueByArtist, revenueByDay } from '@/domain/reportingEngine';
import type { StudioId } from '@/domain/types';
import { dayKey } from '@/utils/studioDay';

/**
 * The Day sheet's turnover tile (W7 n4). ONE definition of "a day's turnover",
 * shared with Reporting > Money so the two screens can never disagree:
 *
 * - The headline is COMPLETED work only, read through Reporting's own helper
 *   (`revenueByDay`): each session's sale gross (balance + deposit applied, merch
 *   excluded), on the day it STARTED, split ours-vs-artist by the commission
 *   engine. A late-night session that started on Sunday and checked out on
 *   Monday is Sunday's money, as its artist's payout is (7kv6: paid by the 6pm
 *   clock-off of the day it was worked).
 * - Work still IN THE CHAIR is not turnover yet (no sale has been rung up). It is
 *   shown separately, valued at the booking's price the payout preview uses,
 *   on its start day.
 */
export interface DayTurnover {
  studioId: StudioId;
  dayIso: string;
  /** Completed sessions' gross for the day: equals `revenueByDay` for the studio-day. */
  completedAud: number;
  completedSessions: number;
  /** The house share of `completedAud`. */
  oursAud: number;
  /** The artists' share of `completedAud`. oursAud + artistAud = completedAud. */
  artistAud: number;
  /** Sessions in the chair right now, at their day rate: the "+ $X still in the chair" sub-line. */
  inChairAud: number;
  inChairSessions: number;
}

export function dayTurnover(studioId: StudioId, dayIso: string): DayTurnover {
  const day = dayKey(dayIso);
  const completed = revenueByDay([day], studioId)[0]!;
  const completedSessions = revenueByArtist([day], studioId).reduce((n, a) => n + a.sessions, 0);

  let inChairAud = 0;
  let inChairSessions = 0;
  const { bookings, artists } = useBrainStore.getState();
  for (const b of Object.values(bookings)) {
    if (b.studioId !== studioId || b.status !== 'in-chair' || dayKey(b.start) !== day) continue;
    // B1: the booked price (day rate, else rate card) — a walk-in has no quote.
    inChairAud += bookingPriceAud(b, artists[b.artistId]);
    inChairSessions += 1;
  }

  return {
    studioId,
    dayIso: day,
    completedAud: completed.totalAud,
    completedSessions,
    oursAud: completed.oursAud,
    artistAud: completed.artistAud,
    inChairAud,
    inChairSessions,
  };
}
